import React, { useState, useEffect, useRef } from 'react';
import {
  TableContainer,
  TableRow,
  TableCell,
  Checkbox,
  TableSortLabel,
  Stack,
} from '@mui/material';
import { StyledTable, StyledTableHead, StyledTableBody } from './Table.styled';
import DeleteIcon from '../../../assets/images/delete.svg';
import TextView from '../../TextView';
import { ASSETS } from '../../../utils/config';
import '../../../App.css';
import EditIcon from '@mui/icons-material/Edit';
import { colors } from '../../../utils';
import { CircularProgress } from '@mui/material';
import { deleteMediaFiles, getMediaByCategory } from '../../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  setVideos,
  setAudios,
  setDocuments,
  setImages,
  setWheels,
} from '../../../redux/reducers/storage';
import { useNavigate } from 'react-router-dom';
import PurpleDoc from '../../../assets/images/PurpleDoc.svg';
import moment from 'moment/moment';
import { closeDialogPopup, openDialogPopup } from '../../../redux/reducers/dialogPopup';


const parseSizeToBytes = (sizeStr) => {
  if (!sizeStr || sizeStr === '0 bytes') return 0;

  const [value, unit] = sizeStr.split(' ');
  const numValue = parseFloat(value);

  switch (unit) {
    case 'bytes':
      return numValue;
    case 'KB':
      return numValue * 1024;
    case 'MB':
      return numValue * 1024 * 1024;
    case 'GB':
      return numValue * 1024 * 1024 * 1024;
    default:
      return 0;
  }
};

const formatTotalSize = (bytes) => {
  if (bytes === 0) return '0 bytes';
  const k = 1024;
  const sizes = ['bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const StorageTable = ({ type, fetchMedia, onEditClick, calculateSize = false }) => {
  const [selectedRows, setSelectedRows] = useState({
    images: [],
    videos: [],
    documents: [],
    audios: [],
    wheels: [],
  });
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [orderBy, setOrderBy] = useState('name');
  const [paths, setPaths] = useState({
    images: [],
    videos: [],
    documents: [],
    audios: [],
    wheels: [],
  });
  const [nextToken, setNextToken] = useState(null);
  const [selectedWheel, setSelectedWheel] = useState(null);
  const dispatch = useDispatch();
  const lastItemRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const data = useSelector((state) => {
    switch (type) {
      case 'images':
        return state.storage.images ?? [];
      case 'videos':
        return state.storage.videos ?? [];
      case 'documents':
        return state.storage.documents ?? [];
      case 'audios':
        return state.storage.audios ?? [];
      case 'wheels':
        return state.storage.wheels ?? [];
      default:
        return [];
    }
  });

  const dispatchMedia = (type, newData, append = false) => {
    const prevData = append ? data : [];
    const updatedData = [...prevData, ...newData];

    switch (type) {
      case 'images':
        dispatch(setImages(updatedData));
        break;
      case 'videos':
        dispatch(setVideos(updatedData));
        break;
      case 'documents':
        dispatch(setDocuments(updatedData));
        break;
      case 'audios':
        dispatch(setAudios(updatedData));
        break;
      case 'wheels':
        dispatch(setWheels(updatedData));
        break;
      default:
        break;
    }
  };

  const fetchTypeMedia = async (pageToken = null) => {
    try {
      if (pageToken) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const response = await getMediaByCategory(type, pageToken);
      const pagination = response.data.data.pagination.nextToken;

      // Append new data if fetching a subsequent page
      if (pageToken) {
        dispatchMedia(type, response.data.data.data, true);
      } else {
        dispatchMedia(type, response.data.data.data);
      }

      // Update nextToken for pagination
      setNextToken(pagination);
    } catch (err) {
      console.error('Failed to fetch Type Media:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Setup intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && nextToken && !loadingMore) {
          fetchTypeMedia(nextToken);
        }
      },
      { threshold: 1.0 }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (lastItemRef.current) {
        observer.unobserve(lastItemRef.current);
      }
    };
  }, [nextToken, loadingMore]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchTypeMedia();
      setLoading(false);
    };

    fetchData();
  }, [type]);

  const handleNextPage = () => {
    if (nextToken) {
      fetchTypeMedia(nextToken);
    }
  };

  // const handlePreviousPage = () => {
  //   if (currentPage > 1) {
  //     // Fetch previous page (you may need to store previous tokens in state)
  //     // This is a simplified example; you may need to adjust based on your API
  //     fetchTypeMedia();
  //     setCurrentPage((prev) => prev - 1);
  //   }
  // };

  // Rest of your component code remains the same...
  // Get selected rows for the current table type
  const selectedForType = selectedRows[type] || [];

  // Determine if all or some rows are selected for the current type
  const allSelected = selectedForType.length === data.length && data.length > 0;
  const someSelected = selectedForType.length > 1;


  const handleDeleteMedia = async (path = null) => {


    dispatch(
      openDialogPopup({
        isOpen: true,
        title: "Delete from Storage",
        primaryButtonTitle: "Confirm",
        secondaryButtonTitle: "Cancel",
        text: "Wheels deleted from Storage cannot be recovered. Are you sure you wish to proceed?",
        secondaryButtonOnClick: () => dispatch(closeDialogPopup()),
        primaryButtonOnClick: async () => {

          let currentPaths = paths[type];

          // If no paths are selected, use the provided path (single row delete)
          if ((!currentPaths || currentPaths.length === 0) && path) {
            currentPaths = [path];
          }

          if (!currentPaths || currentPaths.length === 0) {
            console.warn('No media selected for deletion.');
            return;
          }

          console.log('Deleting media:', currentPaths);

          try {
            const response = await deleteMediaFiles(currentPaths);
            console.log('Delete response:', response);

            if (response.data && response.data.success) {
              setSelectedRows((prev) => ({ ...prev, [type]: [] })); // Reset selection
              setPaths((prev) => ({ ...prev, [type]: [] }));
              fetchMedia();
              fetchTypeMedia();
            } else {
              console.warn('Deletion failed:', response.data);
            }
          } catch (error) {
            console.error('Error deleting media:', error.response ? error.response.data : error.message);
          }
          dispatch(closeDialogPopup());
        },
      })
    );



  };

  const nav = useNavigate();

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort the data based on order
  const sortedData = React.useMemo(() => {
    if (!data.length) return []; // Return an empty array if data is empty

    return [...data].sort((a, b) => {
      let valueA, valueB;

      switch (orderBy) {
        case 'size':
          valueA = parseSizeToBytes(a.size || '0 bytes');
          valueB = parseSizeToBytes(b.size || '0 bytes');
          break;

        case 'views':
          valueA = parseInt(a.total_views || 0);
          valueB = parseInt(b.total_views || 0);
          break;

        case 'created':
          valueA = new Date(a.created_at).getTime();
          valueB = new Date(b.created_at).getTime();
          break;

        case 'name':
          valueA = (type === 'wheels' ? a.wheel_name : a.name || '').toLowerCase();
          valueB = (type === 'wheels' ? b.wheel_name : b.name || '').toLowerCase();
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (valueA == null) return 1;
      if (valueB == null) return -1;

      // Perform the comparison
      if (orderBy === 'name') {
        return order === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        return order === 'asc'
          ? valueA - valueB
          : valueB - valueA;
      }
    });
  }, [data, order, orderBy, type]);

  // Handle row selection
  const handleRowSelect = (index, e) => {
    e.stopPropagation(); // Prevent event from bubbling up to the row
    setSelectedRows((prev) => {
      const prevSelected = prev[type] || []; // Ensure it's an array
      const newSelected = prevSelected.includes(index)
        ? prevSelected.filter((i) => i !== index) // Deselect row
        : [...prevSelected, index]; // Select row

      // Update paths accordingly
      setPaths((prevPaths) => {
        const prevPathsForType = prevPaths[type] || []; // Ensure it's an array
        const newPaths = prevSelected.includes(index)
          ? prevPathsForType.filter((_, i) => prevSelected[i] !== index) // Remove deselected path
          : [...prevPathsForType, type === 'wheels' ? data[index]?.id : data[index]?.path].filter(
            Boolean
          ); // Add new path or id

        return { ...prevPaths, [type]: newPaths };
      });

      return { ...prev, [type]: newSelected };
    });
  };

  // Handle "Select All"
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIndices = data.map((_, index) => index);
      setSelectedRows((prev) => ({ ...prev, [type]: allIndices }));

      // Update paths for the current type
      setPaths((prevPaths) => ({
        ...prevPaths,
        [type]: data.map((item) => (type === 'wheels' ? item.id : item.path)).filter(Boolean),
      }));
    } else {
      setSelectedRows((prev) => {
        const updatedRows = { ...prev };
        delete updatedRows[type]; // Remove only the selected rows of the specific type
        return updatedRows;
      });

      // Clear paths for the current type
      setPaths((prevPaths) => {
        const updatedPaths = { ...prevPaths };
        delete updatedPaths[type]; // Remove paths for the specific type
        return updatedPaths;
      });
    }
  };

  const calculateTotalSelectedSize = () => {
    if (!calculateSize) return null;

    const selectedIndices = selectedRows[type] || [];
    const totalBytes = selectedIndices.reduce((total, index) => {
      const item = data[index];
      if (item && item.size) {
        return total + parseSizeToBytes(item.size);
      }
      return total;
    }, 0);

    return formatTotalSize(totalBytes);
  };

  if (!sortedData.length) {
    return (
      <Stack display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} my={10}>
        <img src={PurpleDoc} alt="purpleDoc" style={{ height: '50px', width: '50px', marginBottom: '5px' }} />
        <TextView
          style={{
            fontSize: '20px',
            fontWeight: '700',
            textAlign: 'center',
            color: colors.darkGrey,
          }}
        >
          No data available
        </TextView>
      </Stack>
    );
  }

  console.log(sortedData, "sortedData")
  return (
    <>
      <div>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <div>
            {calculateSize && selectedRows[type]?.length > 0 && (
              <div style={{ marginBottom: '10px', textAlign: 'right' }}>
                <TextView color="#565556" style={{ fontWeight: '500', fontSize: '14px' }}>
                  Total Selected Size: {calculateTotalSelectedSize()}
                </TextView>
              </div>
            )}
            <TableContainer>
              <StyledTable>
                <StyledTableHead>
                  <TableRow>
                    <TableCell style={{ padding: '5px' }}>
                      {/* MASTER CHECKBOX: "Select All" */}
                      <Checkbox
                        sx={{
                          color: '#9B5AF8',
                          '&.Mui-checked': {
                            color: '#9B5AF8',
                          },
                          '&.MuiCheckbox-root.Mui-checked:hover, &.Mui-checked:hover': {
                            backgroundColor: 'rgba(155, 90, 248, 0.08)',
                          },
                        }}
                        checked={selectedForType.length === data.length && data.length > 0}
                        indeterminate={
                          selectedForType.length > 0 && selectedForType.length < data.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </TextView>
                      </TableSortLabel>
                    </TableCell>

                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'views'}
                        direction={orderBy === 'views' ? order : 'asc'}
                        onClick={() => handleRequestSort('views')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          Views
                        </TextView>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'created'}
                        direction={orderBy === 'created' ? order : 'asc'}
                        onClick={() => handleRequestSort('created')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          Created
                        </TextView>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ padding: '5px' }}>
                      <TableSortLabel
                        active={orderBy === 'size'}
                        direction={orderBy === 'size' ? order : 'asc'}
                        onClick={() => handleRequestSort('size')}
                      >
                        <TextView
                          variant="subtitle"
                          color="#8F9094"
                          style={{ fontWeight: '400', fontSize: '12px' }}
                        >
                          Size
                        </TextView>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ padding: '5px' }}>
                      <TextView
                        variant="subtitle"
                        color="#8F9094"
                        style={{ fontWeight: '400', fontSize: '12px' }}
                      >
                        Action
                      </TextView>
                    </TableCell>

                    {someSelected && (
                      <TableCell style={{ padding: '5px' }}>
                        <img
                          src={DeleteIcon}
                          onClick={() => handleDeleteMedia()}
                          alt="Delete Icon"
                          style={{
                            filter:
                              'invert(13%) sepia(95%) saturate(7493%) hue-rotate(3deg) brightness(95%) contrast(120%)',
                            cursor: 'pointer',
                            // marginTop: '25px',
                            // marginLeft: '-60px',
                            // marginRight: '30px',
                            // alignSelf: 'center',
                          }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                </StyledTableHead>

                <StyledTableBody>
                  {sortedData.map((item, index) => (
                    <TableRow
                      key={index}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        calculateSize ? null :
                          onEditClick({ id: item.id, name: item.wheel_name, image: item.wheel_preview, size: item.size })
                      }
                      ref={index === sortedData.length - 1 ? lastItemRef : null}
                    >
                      <TableCell style={{ padding: '5px' }}>
                        {/* Per-row checkbox */}
                        <Checkbox
                          sx={{ '&.Mui-checked': { color: '#9B5AF8' } }}
                          checked={(selectedRows[type] || []).includes(index)}
                          onChange={(e) => handleRowSelect(index, e)}
                          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
                        />
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {/* {getIcon(type, item.url)} */}
                          <img
                            alt="default"
                            src={item?.wheel_preview || ASSETS.DialogImg}
                            style={{ marginRight: '10px', height: '35px', width: '35px', borderRadius: '5px' }}
                          />
                          <TextView color="#565556" style={{ fontWeight: '500', fontSize: 12 }}>
                            {type === 'wheels'
                              ? item.wheel_name.length > 30
                                ? `${item.wheel_name.replace(/<[^>]*>/g, '').substring(0, 30)}...`
                                : item.wheel_name.replace(/<[^>]*>/g, '')
                              : item.name.length > 30
                                ? `${item.name.substring(0, 30)}...`
                                : item.name}
                          </TextView>
                        </div>
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <TextView color="#565556" style={{ fontWeight: '500', fontSize: '12px' }}>
                          {item.total_views}
                        </TextView>
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <TextView color="#565556" style={{ fontWeight: '500', fontSize: '12px' }}>
                          {moment(item.created_at).format('MM/DD/YYYY')}
                        </TextView>
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        <TextView color="#565556" style={{ fontWeight: '500', fontSize: '12px' }}>
                          {item.size}
                        </TextView>
                      </TableCell>
                      <TableCell style={{ padding: '5px' }}>
                        {type === 'wheels' && (
                          <EditIcon
                            onClick={() =>
                              // onEditClick({ id: item.id, name: item.wheel_name, image: item.wheel_preview })
                              nav(`/wheel/${item?.id}`)
                            }
                            style={{
                              fontSize: '24px',
                              color: '#BBBBBB',
                              cursor: allSelected ? 'not-allowed' : 'pointer',
                            }}
                          />
                        )}
                        {selectedForType.length > 1 ? null : (
                          <img
                            src={DeleteIcon}
                            alt="Delete Icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              // setShowDeleteModal(true)
                              handleDeleteMedia(type === 'wheels' ? item.id : item.path);
                            }}
                            style={{
                              filter: 'invert(13%) sepia(95%) saturate(7493%) hue-rotate(3deg) brightness(95%) contrast(120%)',
                              cursor: selectedForType.length > 1 ? 'not-allowed' : 'pointer',
                            }}
                          />
                        )}
                      </TableCell>

                      {allSelected && <TableCell style={{ padding: '5px' }}></TableCell>}
                    </TableRow>
                  ))}
                </StyledTableBody>
              </StyledTable>

              {/* Pagination Controls */}
              {/* <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Button onClick={handleNextPage} disabled={!nextToken}>
                  See More
                </Button>
              </div> */}
            </TableContainer>
            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <CircularProgress size={24} />
              </div>
            )}
          </div>
        )}
      </div>
      {/* <DialogModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Cancel Subscription"
        message="Are you sure you want to delete these files?"
        primaryButton={"Confirm"}
        secondaryButton={"Cancel"}
        onPrimaryPress={handleDeleteMedia}
        onSecondaryPress={() => setShowDeleteModal(false)}
      /> */}
    </>
  );
};

export default StorageTable;
